import Brand, { type IBrand } from "./Brand";
import Category, { type ICategory } from "./Category";
import ProductImage, {
  type IProductImage,
  type ProductImageSizeType,
} from "./ProductImage";
import OfferMonthDetail, { type IOfferMonthDetail } from "./OfferMonthDetail";
import type Coupon from "./Coupon";
import type { ICoupon } from "./Coupon";
import SpecialCharacteristic, {
  type ISpecialCharacteristic,
} from "./SpecialCharacteristic";
import ProductReview from "./ProductReview";
import type { Router } from "vue-router";
import { getProductRouteLocation } from "@/utils/productRoute";

export interface IAttributeGroupMap {
  group: {
    id: string | null;
    name: string | null;
  };
  attributes: {
    is_web_filter: boolean;
    attribute_id: string | null;
    attribute_name: string | null;
    value_id: string | null;
    value: string | null;
  }[];
}

export interface IStoreWithStock {
  store_id: string | null;
  store_name: string;
  qty: number;
  can_other_stores_take_stock: boolean;
  fast_pickup: boolean;
}

export interface IMonthlyOfferStores {
  min_months: number;
  max_months: number;
  with_discount: boolean;
  with_interest: boolean;
  with_wallet: boolean;
  without_interest: boolean;
  title: string;
  stores: string[];
}

export interface IProduct {
  id: string | null;
  name: string | null;
  slug: string | null;
  seo_part_number: string | null;
  seo_descriptive_title: string | null;
  canonical_path: string | null;
  part_number: string | null;
  category: ICategory | null;
  brand: IBrand | null;
  images: IProductImage[];
  coverImage: IProductImage | null;
  rating: number;
  reviews_count: number;
  price_with_tax: number;
  current_price_with_tax: number;
  mwi_one_price_with_tax: number;
  set_base_price_with_tax: number;
  mwi_one_disc_percent: number;
  mwi_wallet_percent: number;
  mwi_wallet_amount_with_tax: number;
  is_kit: boolean;
  is_assembly: boolean;
  is_component: boolean;
  is_web_purchasable: boolean;
  offer_months: IOfferMonthDetail[];
  stock_stores: number;
  stock_providers: number;
  wallet_promotion_amount: number;
  has_shipping_promotion: boolean;
  has_last_stock: boolean;
  stock_total: number;
  price_valid_until_utc: string | null;
  coupon: ICoupon | null;
  short_description: string | null;
  description: string | null;
  description_file: string | null;
  description_file_is_pdf: boolean;
  description_file_is_image: boolean;
  description_landing: string | null;
  video_link: string | null;
  manual_file: string | null;
  specs_file: string | null;
  manufacturer_link: string | null;
  meta_description: string | null;
  attribute_group_map: Record<string, IAttributeGroupMap>;
  characteristics: ISpecialCharacteristic[];
  bullet_points: string[];
  any_related: {
    related: IProduct[];
    substitutes: IProduct[];
    sets: IProduct[];
    components: IProduct[];
  };
  stores_with_stock: IStoreWithStock[];
  upc_gtin: string | null;
  created_utc: string | null;
  warranty_days: number;
  monthly_offer_stores_info: IMonthlyOfferStores[];
  performance: number | null;
  compatible?: number | null;
  assembly_group?: string | null;
  assembly_group_configs?:
    | Array<{ name: string; id: string; max?: number; note?: string }>
    | [];
  related_assembly_groups?:
    | Array<{ name: string; id: string; order: number }>
    | [];
}

export default class Product implements IProduct {
  public type: string = "products";
  public relationshipNames: string[] = [
    "brand",
    "category",
    "category",
    "images",
    "characteristics",
    "coverImage",
  ];

  public id: string | null = null;
  public name: string | null = null;
  public slug: string | null = null;
  public seo_part_number: string | null = null;
  public seo_descriptive_title: string | null = null;
  public canonical_path: string | null = null;
  public part_number: string | null = null;
  public category: Category | null = null;
  public brand: Brand | null = null;
  public images: ProductImage[] = [];
  public coverImage: ProductImage | null = null;
  public rating: number = 0;
  public reviews_count: number = 0;
  public price_with_tax: number = 0;
  public current_price_with_tax: number = 0;
  public mwi_one_price_with_tax: number = 0;
  public set_base_price_with_tax: number = 0;
  public mwi_one_disc_percent: number = 0;
  public mwi_wallet_percent: number = 0;
  public mwi_wallet_amount_with_tax: number = 0;
  public is_kit: boolean = false;
  public is_assembly: boolean = false;
  public is_component: boolean = false;
  public is_web_purchasable: boolean = false;
  public offer_months: OfferMonthDetail[];
  public stock_stores: number = 0;
  public stock_providers: number = 0;
  public wallet_promotion_amount: number = 0;
  public has_shipping_promotion: boolean = false;
  public has_last_stock: boolean = false;
  public stock_total: number = 0;
  public price_valid_until_utc: string | null = null;
  public coupon: Coupon | null = null;
  public short_description: string | null = null;
  public description: string | null = null;
  public description_file: string | null = null;
  public description_file_is_pdf: boolean = false;
  public description_file_is_image: boolean = false;
  public description_landing: string | null = null;
  public meta_description: string | null = null;
  public attribute_group_map: Record<string, IAttributeGroupMap> = {};
  public video_link: string | null = null;
  public manual_file: string | null = null;
  public specs_file: string | null = null;
  public manufacturer_link: string | null = null;
  public characteristics: SpecialCharacteristic[] = [];
  public bullet_points: string[] = [];
  public any_related: {
    related: Product[];
    substitutes: Product[];
    sets: Product[];
    components: Product[];
  };

  public stores_with_stock: IStoreWithStock[];
  public related: Product[] = [];
  public substitutes: Product[] = [];
  public sets: Product[] = [];
  public components: Product[] = [];
  public page: number = 1;
  public upc_gtin: string | null = null;
  public reviews: ProductReview[] = [];
  public created_utc: string | null = null;
  public warranty_days: number = 0;
  public monthly_offer_stores_info: IMonthlyOfferStores[] = [];
  public performance: number | null = null;
  public compatible: number | null = null;
  public assembly_group?: string | null = null;
  public assembly_group_configs:
    | Array<{ name: string; id: string; max?: number; note?: string }>
    | [] = [];

  public related_assembly_groups:
    | Array<{ name: string; id: string; order: number }>
    | [] = [];

  public reviews_page: number = 0; // 0 porque se suma antes de hacer fetch

  /** ID para el componente general del producto, sirve cómo buena práctica de programación al mantener la esencia de reciclar componentes incluso los de tipo Page. */
  public readonly componentId: string;

  public constructor({
    id,
    name,
    slug,
    seo_part_number,
    seo_descriptive_title,
    canonical_path,
    part_number,
    category,
    brand,
    images,
    coverImage,
    rating,
    reviews_count,
    price_with_tax,
    current_price_with_tax,
    mwi_one_price_with_tax,
    set_base_price_with_tax,
    mwi_one_disc_percent,
    mwi_wallet_percent,
    mwi_wallet_amount_with_tax,
    is_kit,
    is_assembly,
    is_component,
    is_web_purchasable,
    offer_months,
    stock_stores,
    stock_providers,
    stock_total,
    wallet_promotion_amount,
    has_shipping_promotion,
    has_last_stock,
    coupon,
    short_description,
    description,
    description_file,
    description_file_is_pdf,
    description_file_is_image,
    description_landing,
    meta_description,
    attribute_group_map,
    video_link,
    manual_file,
    specs_file,
    manufacturer_link,
    characteristics,
    bullet_points,
    any_related,
    stores_with_stock,
    upc_gtin,
    price_valid_until_utc,
    created_utc,
    warranty_days,
    monthly_offer_stores_info,
    performance,
    compatible,
    related_assembly_groups,
    assembly_group_configs,
    assembly_group,
  }: IProduct) {
    this.id = id ? id + "" : null;
    this.name = name;
    this.slug = slug;
    this.seo_part_number = seo_part_number || null;
    this.seo_descriptive_title = seo_descriptive_title || null;
    this.canonical_path = canonical_path || null;
    this.part_number = part_number;
    this.brand = brand ? new Brand(brand as IBrand) : null;
    this.category = category ? new Category(category as ICategory) : null;
    this.images = Array.isArray(images) ? ProductImage.fromArray(images) : [];
    this.coverImage = coverImage ? new ProductImage(coverImage) : null;
    this.rating = rating || 0;
    this.reviews_count = reviews_count * 1 || 0;
    this.price_with_tax = price_with_tax * 1 || 0;
    this.current_price_with_tax = current_price_with_tax || 0;
    this.mwi_one_price_with_tax = mwi_one_price_with_tax || 0;
    this.set_base_price_with_tax = set_base_price_with_tax || 0;
    this.mwi_one_disc_percent = mwi_one_disc_percent || 0;
    this.mwi_wallet_percent = mwi_wallet_percent || 0;
    this.mwi_wallet_amount_with_tax = mwi_wallet_amount_with_tax || 0;
    this.is_kit = !!is_kit;
    this.is_assembly = !!is_assembly;
    this.is_component = !!is_component;
    this.is_web_purchasable = !!is_web_purchasable;
    this.offer_months = Array.isArray(offer_months)
      ? OfferMonthDetail.fromArray(offer_months)
      : [];
    this.stock_stores = Number(stock_stores) || 0;
    this.stock_providers = Number(stock_providers) || 0;
    const parsedStockTotal = Number(stock_total);
    this.stock_total =
      stock_total === null ||
      stock_total === undefined ||
      stock_total === "" ||
      Number.isNaN(parsedStockTotal)
        ? this.stock_stores + this.stock_providers
        : parsedStockTotal;
    this.price_valid_until_utc = price_valid_until_utc || null;
    this.wallet_promotion_amount = wallet_promotion_amount * 1 || 0;
    this.has_shipping_promotion = !!has_shipping_promotion;
    this.has_last_stock = !!has_last_stock;
    this.coupon = coupon || null;
    this.short_description = short_description || null;
    this.description = description || null;
    this.description_file = description_file || null;
    this.description_file_is_pdf = !!description_file_is_pdf;
    this.description_file_is_image = !!description_file_is_image;
    this.description_landing = description_landing || null;
    this.meta_description = meta_description;
    this.attribute_group_map = attribute_group_map || {};
    this.video_link = video_link || null;
    this.manual_file = manual_file || null;
    this.specs_file = specs_file || null;
    this.manufacturer_link = manufacturer_link || null;
    this.characteristics = Array.isArray(characteristics)
      ? SpecialCharacteristic.fromArray(characteristics)
      : [];
    this.bullet_points = bullet_points || [];
    this.any_related = {
      related: Array.isArray(any_related?.related)
        ? Product.fromArray(any_related.related)
        : [],
      substitutes: Array.isArray(any_related?.substitutes)
        ? Product.fromArray(any_related.substitutes)
        : [],
      sets: Array.isArray(any_related?.sets)
        ? Product.fromArray(any_related.sets)
        : [],
      components: Array.isArray(any_related?.components)
        ? Product.fromArray(any_related.components)
        : [],
    };
    this.stores_with_stock = stores_with_stock || [];
    this.upc_gtin = upc_gtin;
    this.created_utc = created_utc || null;
    this.warranty_days = warranty_days * 1 || 0;
    this.monthly_offer_stores_info = monthly_offer_stores_info || [];
    this.performance = performance || null;
    this.compatible = compatible || null;
    this.related_assembly_groups = related_assembly_groups || [];
    this.assembly_group_configs = assembly_group_configs || [];
    this.assembly_group = assembly_group || null;

    this.componentId = "product-page-" + this.id;
  }

  public getImagesOfSize(
    size: ProductImageSizeType,
    isCover?: boolean,
  ): string[] {
    if (!this.images.length) {
      return [];
    }
    let firstIsCover = false;
    const result = this.images.map((image, index) => {
      if (isCover && !image.is_cover && index > 0) {
        // take first image always as cover
        return null;
      }
      if (index === 0) {
        firstIsCover = image.is_cover;
      }
      return image.getBestImageOfSize(size);
    });
    // Switch the first image if this is not a cover when cover is required and there are more real covers.
    if (!firstIsCover && result.length > 1) {
      const aux = result[0];
      result[0] = result[1];
      result[1] = aux;
    }
    return result.filter((url) => url != null).map((url) => url as string);
  }

  public getCoverImage(
    size: ProductImageSizeType = ProductImage.Sizes.Medium,
    useCover404: boolean = false,
  ): string | undefined {
    if (this.coverImage) {
      return this.coverImage.getBestImageOfSize(size);
    }
    // Por alguna razón Sentry reporta que a veces esta función no existe, por eso me veo obligado a evaluarla antes de seguir.
    if (typeof this.getImagesOfSize !== "function") {
      return "/images/imagen-no-disponible.png";
    }
    const url = this.getImagesOfSize(size, true).at(0);
    return useCover404 || !url ? "/images/imagen-no-disponible.png" : url;
  }

  public getPercentDiscount(): number {
    return parseInt(
      (
        (1 - this.getFinalCurrentPrice() / this.getFinalCommonPrice()) *
        100
      ).toFixed(0),
    );
  }

  public getPercentDiscountText(): string {
    return this.getPercentDiscount() + "%";
  }

  public static fromArray(items: IProduct[]) {
    return items.map((item) => new Product(item));
  }

  public hasDiscount() {
    return (
      this.current_price_with_tax < this.price_with_tax ||
      this.mwi_one_disc_percent > 0
    );
  }

  public getFinalCommonPrice() {
    return this.mwi_one_disc_percent > 0
      ? this.current_price_with_tax
      : this.price_with_tax;
  }

  public getFinalCurrentPrice() {
    return this.mwi_one_disc_percent > 0
      ? this.mwi_one_price_with_tax
      : this.current_price_with_tax;
  }

  public async fetchMoreReviews() {
    if (!this.id) {
      return this.reviews;
    }
    this.reviews_page += 1;
    const { data: newReviews } = await ProductReview.fetchByProduct(
      this.id,
      this.reviews_page,
    );
    this.reviews.push(...newReviews);
    return this.reviews;
  }

  public resolveLink(router: Router) {
    return router.resolve(this.resolveLocation());
  }

  public resolveLocation() {
    return getProductRouteLocation(this);
  }

  public mergeFromOther(from: IProduct) {
    Object.keys(from).forEach((key) => {
      const oldObj = this as unknown as Record<string, unknown>;
      const newObj = from as unknown as Record<string, unknown>;
      if (
        (Array.isArray(newObj[key]) && newObj[key].length > 0) ||
        (!Array.isArray(newObj[key]) && newObj[key])
      ) {
        oldObj[key] = newObj[key];
      }
    });
  }

  public toObject(): IProduct {
    const target = Object.create({});
    const object = this as unknown as Record<string, unknown>;
    Object.keys(this).forEach((key) => {
      if (object[key] instanceof Map) {
        target[key] = Object.fromEntries(object[key]);
      } else {
        target[key] = object[key];
      }
    });
    return target as IProduct;
  }

  public toJson() {
    return JSON.stringify(this.toObject());
  }
}
